// Persists window geometry per /fullscreen route so the Operations multi-window
// preset can re-open each window where the dispatcher last placed it on their
// monitors. The popup writes its own size/position to localStorage (shared with
// the opener since same-origin); the opener reads it when spawning.

import { useEffect } from "react";

const KEY = (path) => `irisguard.popout.geom:${path}`;
const KEY_PREFIX = "irisguard.popout.geom:";

// Wipe every saved popup geometry — used by the sidebar "reset layout"
// gesture (Shift+click on the Pop-out button) when monitors have changed
// and stored positions would spawn windows off-screen.
export function clearAllGeometry() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(KEY_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
    return keys.length;
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.warn("clearAllGeometry failed:", err);
    return 0;
  }
}

export function readGeometry(path) {
  try {
    const raw = localStorage.getItem(KEY(path));
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (
      typeof v?.w === "number" && v.w > 200 &&
      typeof v?.h === "number" && v.h > 200 &&
      typeof v?.x === "number" && typeof v?.y === "number"
    ) return v;
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.warn("readGeometry parse failed:", err);
  }
  return null;
}

function saveCurrent(path) {
  try {
    localStorage.setItem(
      KEY(path),
      JSON.stringify({
        x: window.screenX,
        y: window.screenY,
        w: window.outerWidth,
        h: window.outerHeight,
      }),
    );
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.warn("saveCurrent geometry failed:", err);
  }
}

// React hook — call inside any /fullscreen page; persists current geometry
// every 2s while the popup is open and once on unload.
export function useRememberWindowGeometry(path) {
  useEffect(() => {
    if (!path) return undefined;
    const tick = () => saveCurrent(path);
    tick();
    const id = setInterval(tick, 2000);
    window.addEventListener("beforeunload", tick);
    return () => {
      clearInterval(id);
      window.removeEventListener("beforeunload", tick);
      tick();
    };
  }, [path]);
}
